import { isDefined } from '@sgme/fp';
import type { FieldMetadata } from 'models/forms/fields';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { FC, useCallback } from 'react';

import { UnknownFieldWarning } from '../UnknownField';
import { GridClientColumn } from './GridClientColumn';
import { GridDateColumn } from './GridDateColumn';
import { GridListColumn } from './GridListColumn';
import { GridPercentColumn } from './GridPercentColumn';
import { GridTextColumn } from './GridTextColumn';

export type RowData = Record<string, unknown>;

interface ColumnsProps {
  row: RowData;
  rowIndex: number;
  fieldId: string;

  metadata: FieldMetadata[];

  setRowValue: (rowIndex: number, value: RowData) => void;
}

export function getDefaultValueForNewRow(meta: FieldMetadata) {
  switch (meta.type) {
    case 'ListField':
      return (meta.availableItems?.length ?? 0) > 0 ? meta.availableItems[0].value : null;
    default:
      return null;
  }
}

export const GridColumns: FC<ColumnsProps> = ({ fieldId, row, metadata, rowIndex, setRowValue }) => {
  const onChange = useCallback(
    (id: string, value: unknown) => {
      setRowValue(rowIndex, { ...row, [id]: value });
    },
    [row, rowIndex, setRowValue],
  );
  if (!isDefined(metadata)) {
    throw new Error(`Missing column metadata for field ${fieldId}`);
  }
  return (
    <>
      {metadata
        .filter(isDefined)
        .map(columnMetadata => {
          const { id } = columnMetadata;
          const value = row[id];

          switch (columnMetadata.type) {
            case 'DateField':
              return (
                <GridDateColumn
                  key={id}
                  id={id}
                  value={value as Nullable<string>}
                  setCellValue={onChange}
                  isClearable={columnMetadata.isClearable ?? false}
                />
              );
            case 'PercentField':
              return (
                <GridPercentColumn
                  key={id}
                  id={id}
                  value={value as string}
                  setCellValue={onChange}
                  placeholderKey={columnMetadata.placeholderKey}
                />
              );
            case 'ClientListField':
              return (
                <GridClientColumn
                  key={id}
                  id={id}
                  value={value as Nullable<ISalesCounterpart>}
                  setCellValue={onChange}
                />
              );

            case 'ListField':
              const availableItems = columnMetadata.availableItems!;
              return (
                <GridListColumn
                  key={id}
                  id={id}
                  value={value as Nullable<string>}
                  availableItems={availableItems}
                  setCellValue={onChange}
                />
              );
            case 'TextField':
              return (
                <GridTextColumn
                  key={id}
                  id={id}
                  value={value as string}
                  setCellValue={onChange}
                  placeholderKey={columnMetadata.placeholderKey}
                />
              );
            default:
              return <UnknownFieldWarning key={id} id={columnMetadata.id} value={columnMetadata.type} />;
          }
        })
        .map((col, idx) => (
          <div key={col.key || idx} className="flex-grow-1 d-flex flex-row flex-nowrap">
            {col}
          </div>
        ))}
    </>
  );
};

GridColumns.displayName = 'GridColumns';
